import React, { useMemo, useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SEASONS } from '../../utils/constants';
import { LifeStats } from '../../hooks/useLifeStats';
import { springConfig } from '../../utils/animations';

interface YearsGridProps {
  stats: LifeStats;
}

const YearsGrid: React.FC<YearsGridProps> = ({ stats }) => {
  const prefersReducedMotion = useReducedMotion();
  const [hasAnimated, setHasAnimated] = useState(false);

  // Track if we've done the initial animation
  useEffect(() => {
    if (!hasAnimated) {
      const timer = setTimeout(() => setHasAnimated(true), 2500);
      return () => clearTimeout(timer);
    }
  }, [hasAnimated]);

  // Memoize grid data
  const gridData = useMemo(() => {
    if (stats.lifeExpectancy <= 0) return null;

    const years = Math.ceil(stats.lifeExpectancy);
    const grid = [];

    for (let year = 0; year < years; year++) {
      const isPast = year < stats.yearsLived;
      const isCurrent = year === Math.floor(stats.yearsLived);
      const season = SEASONS.find(s => year >= s.start && year <= s.end);

      let bgColor = "bg-gray-100 dark:bg-catppuccin-surface1";

      if (season && isPast) {
        if (season.name === "Spring") bgColor = "bg-green-200 dark:bg-green-700";
        else if (season.name === "Summer") bgColor = "bg-yellow-200 dark:bg-yellow-700";
        else if (season.name === "Autumn") bgColor = "bg-orange-200 dark:bg-orange-700";
        else if (season.name === "Winter") bgColor = "bg-blue-200 dark:bg-blue-700";
      }

      if (isCurrent) bgColor = "bg-blue-500 dark:bg-catppuccin-blue text-white";

      grid.push({
        year,
        isPast,
        isCurrent,
        bgColor,
        season
      });
    }

    return grid;
  }, [stats.lifeExpectancy, stats.yearsLived]);

  if (!gridData) return null;

  // Container animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.015,
        delayChildren: 0.1
      }
    }
  };

  // Cell animation variants
  const cellVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -10 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: springConfig.bouncy
    }
  };

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    return (
      <div className="flex flex-wrap justify-center max-w-5xl">
        {gridData.map((item) => (
          <div
            key={`year-block-${item.year}`}
            className={`w-12 h-12 ${item.bgColor} m-1 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-125 hover:shadow-md hover:z-10`}
            title={item.season ? `Year ${item.year + 1}: ${item.season.name} (${item.season.description})` : `Year ${item.year + 1}`}
          >
            <span className="text-sm font-bold dark:text-catppuccin-text">{item.year + 1}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <motion.div
      className="flex flex-wrap justify-center max-w-5xl"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {gridData.map((item, index) => (
        <motion.div
          key={`year-block-${item.year}`}
          className={`w-12 h-12 ${item.bgColor} m-1 rounded-lg flex items-center justify-center cursor-pointer relative`}
          variants={hasAnimated ? undefined : cellVariants}
          initial={hasAnimated ? undefined : "hidden"}
          animate={hasAnimated ? undefined : "visible"}
          whileHover={{
            scale: 1.15,
            zIndex: 10,
            boxShadow: '0 10px 20px -5px rgba(0, 0, 0, 0.15)'
          }}
          whileTap={{ scale: 0.95 }}
          transition={{
            delay: hasAnimated ? 0 : index * 0.015,
            ...springConfig.snappy
          }}
          title={item.season ? `Year ${item.year + 1}: ${item.season.name} (${item.season.description})` : `Year ${item.year + 1}`}
        >
          {/* Current year glow effect */}
          {item.isCurrent && (
            <motion.div
              className="absolute inset-0 rounded-lg"
              animate={{
                boxShadow: [
                  '0 0 0 0 rgba(59, 130, 246, 0.4)',
                  '0 0 15px 3px rgba(59, 130, 246, 0.3)',
                  '0 0 0 0 rgba(59, 130, 246, 0)'
                ],
                scale: [1, 1.05, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut'
              }}
            />
          )}
          <span className={`text-sm font-bold relative z-10 ${item.isCurrent ? 'text-white' : 'dark:text-catppuccin-text'}`}>
            {item.year + 1}
          </span>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default YearsGrid;
